import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
  NgZone,
  HostListener,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  tap,
  from,
  of,
  ReplaySubject,
  catchError,
  takeUntil,
  filter,
  pairwise,
  startWith,
  Subscription,
  switchMap,
  Observable,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
} from 'rxjs';

import { AppComponent } from 'app/app.component';
import { LaunchDarklyService } from 'app/core/services/launch-darkly/launch-darkly.service';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { EventsService } from 'app/modules/social-events/services/events.service';
import { GuestAuthService } from 'app/services/guest-auth.service';
import { LogService } from 'app/services/logging.service';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { ModalService } from 'app/shared/modals/modal.service';
import { Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanAllStreams } from 'app/utils/media/stream';
import { notNil } from 'app/utils/stream-util';
import Company from 'entity/Company';
import Social from 'entity/Social';
import SocialAttendee from 'entity/SocialAttendee';
import User from 'entity/User';
import {
  IncomingMessageType,
  PeerCandidate,
  SocialEndType,
  IncomingMessage,
  BaseUser,
  ChatGetMessage,
  OutgoingMessageType,
} from 'messages/websocket.messages';
import { SocialEventType } from 'messages/websocket.messages';
import CompanyController from 'rest/CompanyController';

import { DEFAULT_REGION, PLAYER_RADIUS } from '../constants';
import { ModalAccessComponent, ModalAccessData } from '../modal-access/modal-access.component';
import { ModalDialogComponent, ModalDialogData, ModalDialogResult } from '../modal-dialog/modal-dialog.component';
import { ModalNameComponent, ModalNameData } from '../modal-name/modal-name.component';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { ModalSocialInviteComponent } from '../modal-social-invite/modal-social-invite.component';
import { ModalInviteData } from '../modal-social-invite/ModalInviteData';
import PreciateEventHelper from '../preciate-stream/preciate-event-helper';
import { RoomParticipant } from '../preciate-stream/room-participant';
import { BadgingService } from '../services/badging/badging.service';
import { BrowserService } from '../services/browser.service';
import { ChatService } from '../services/chat.service';
import { CleanUpTasksService } from '../services/clean-up-tasks.service';
import { ConvoService } from '../services/convo.service';
import { DebuggerService } from '../services/debugger.service';
import { DeviceService } from '../services/device/device.service';
import { EmoteService } from '../services/emote/emote.service';
import { EntryFlowService } from '../services/entry-flow.service';
import { FavoriteService } from '../services/favorite.service';
import { HeaderMultipleOptionsMessageService } from '../services/header-multiple-options-message.service';
import { HeaderToastMessageService } from '../services/header-toast-message.service';
import { HostService } from '../services/host-service';
import { ModalSocialEventService } from '../services/modal-social-event.service';
import { MultimediaRoomService } from '../services/multimedia-room.service';
import { MultimediaUserService } from '../services/multimedia-user.service';
import { PerformerViewService } from '../services/performer-view.service';
import { PerformerService } from '../services/performer.service';
import { PresentationService } from '../services/presentation/presentation.service';
import { RingColorsService } from '../services/ring-colors/ring-colors.service';
import { RoomLifecycleService } from '../services/room/room-lifecycle.service';
import { RoomPresenceService } from '../services/room/room-presence.service';
import { RoomStateService } from '../services/room/room-state.service';
import { RtcStatsService } from '../services/rtc-stats.service';
import { SceneService } from '../services/scene.service';
import { ShareScreenParticipantService } from '../services/share-screen/share-screen-participant.service';
import { ShareScreenService } from '../services/share-screen/share-screen.service';
import { GroupShareScreenParticipantService } from '../services/share-screen-group/group-share-screen-participant.service';
import { GroupShareScreenService } from '../services/share-screen-group/group-share-screen.service';
import { SideSectionService } from '../services/side-section.service';
import { SubscriptionService } from '../services/subscription/subscription.service';
import { ToastService } from '../services/toast/toast.service';
import { BlackSimpleToasterService } from '../services/toasters/black-simple-toaster.service';
import { TransitionPresentationService } from '../services/transition-presentation.service';
import { TransitionService } from '../services/transition.service';
import { VideoRoomService } from '../services/video-room.service';
import { VideoChatService } from '../services/videochat.service';
import { WebsocketTimerService } from '../services/websocket-timer.service';
import {
  WebsocketService,
  WebsocketStateType,
  DisconnectStatus,
  WebsocketDisconnectType,
} from '../services/websocket.service';
import { LeaveRoomType } from '../social-leave-room/social-leave-room-type';
import { SideContainerType } from '../social-menu/enums/SideContainerType';
import { Player } from '../social-pixi-canvas/player';
import SocialStateHelper from '../social-state-machine/social-state-helper';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import SoundControl from '../sound-control';
import {
  actions,
  DEFAULT_GROUP_SHARE_TIMEOUTS,
  PRESENTATION_V3_TIMEOUTS,
  selectors,
  socialAppSelectors,
  SpecialAccessGroupModalMode,
} from '../state/app';
import { mediaSelectors } from '../state/media';
import { SOCIAL_LICENSE_EXCEEDED, SOCIAL_LICENSE_REQUIRED } from '../strings';

import { ClosedCaptionsService } from './../services/closed-captions/closed-captions.service';
import { SpeechToTextService } from './../services/speech-to-text/speech-to-text.service';
import { fadeOut } from './join-social.animation';
import { fadeInLayerImage } from './presentation-end-animation';
import { fadeLayerImage } from './presentation-start-animation';
import { Renderer } from 'pixi.js';
import _ from 'lodash';
import SocialEvent from 'entity/SocialEvent';

@Component({
  selector: 'app-join-social-home',
  styleUrls: ['./join-social-home.component.scss'],
  templateUrl: './join-social-home.component.html',
  animations: [fadeOut, fadeLayerImage, fadeInLayerImage],
  standalone: false,
})
export class JoinSocialHomeComponent extends Loggable implements OnInit, OnDestroy {
  public social?: Social;
  public user?: User;
  private participantConnectedSubscription?: Subscription;
  private websocketOpenSubscription?: Subscription;

  private destroyed$ = new ReplaySubject<boolean>(1); // used to manage unloading all subscriptions
  private companyController = new CompanyController();

  private isGuest?: boolean;
  private socialId?: string;
  public isSocialLoaded = false;
  public company?: Company;
  public canJoinOrStart = false;
  private leftSocialOnPurpose = false;
  public inSocial = false; // flag to indicate we are joined to the social
  public isHost = false;
  public showLoader = true;
  private myX?: number;
  private myY?: number;
  public overlayText = 'Initializing your Room';
  private overlayTimeout?: Timeout;
  public showLoadingOverlay?: boolean;
  public isPresentationActive = false;

  public isUserPresenting = false;
  public remoteScreenSharePresenter?: User;

  public isLoadingPresentationParticipant = false;

  public overlayPresentationText = '';

  //Welcome Page
  protected pageLoaded = false;

  // Group Share
  public groupParticipantScreenShareActive = false;
  public isPlayerOutsideGroupLimit = false;
  public isUserGroupSharing = false;
  // Timeouts
  public admissionIntervalTimeout?: Timeout;

  public peerConnection: RTCPeerConnection | null = null;

  private didDisconnect = false;

  private useAndroidFullScreen = true;
  public isPerforming = false;
  public someoneIsPerforming = false;

  private leaveRoomSubscription?: Subscription;

  public isBrowser?: boolean;

  private isVideoEnabled = true;
  private isMicEnabled = true;
  private showInviteModal = true;

  // CustomerAPI
  private customerAPIEnabled = false;

  //Capture debug button
  public allowDebugger = false;
  public disableCaptureDebugData = false;
  protected isTransitioning = false;

  protected transitionOverlay = false;
  private isRoomSettingsRecording = false;

  public closedCaptionsFlagEnabled = false;
  private captionsListenersInitialized = false;

  public isBadgingModalOpen = false;
  protected isLoadingPresentation = false;
  private soundControl = SoundControl.getInstance(this.sceneService);

  private aiSummaries = false;

  protected isMobileOrTablet = false;

  protected eventName?: string;
  protected darkModeTimeout = DEFAULT_GROUP_SHARE_TIMEOUTS.darkModeTimeout;
  protected backgroundFadeOutTimeout = PRESENTATION_V3_TIMEOUTS.backgroundFadeOutTimeout;

  protected isSAGModalOpen = false;
  protected isSAGModalRequestAccess = false;
  protected hasCompletedDemo = signal<boolean>(true);
  protected waitingForHostStep = signal<string | undefined>(undefined);
  protected hasWaitedForHost = signal<boolean>(false);

  protected event?: SocialEvent;

  public constructor(
    private readonly videoChatService: VideoChatService,
    private ngZone: NgZone,
    private readonly renderer2: Renderer2,
    private dialog: MatDialog,
    private router: Router,
    private sceneService: SceneService,
    private videoRoomService: VideoRoomService,
    private rtcStatsService: RtcStatsService,
    private headerToastMessageService: HeaderToastMessageService,
    private headerMultipleMessageService: HeaderMultipleOptionsMessageService,
    private convoService: ConvoService,
    private performerService: PerformerService,
    private presentationService: PresentationService,
    private toastService: ToastService,
    private performerViewService: PerformerViewService,
    private deviceService: DeviceService,
    private websocketTimerService: WebsocketTimerService,
    private chatService: ChatService,
    private modalSocialEventService: ModalSocialEventService,
    public shareScreenParticipantService: ShareScreenParticipantService,
    private shareScreenService: ShareScreenService,
    public sideSectionService: SideSectionService,
    private hostService: HostService,
    private multimediaUserService: MultimediaUserService,
    private multimediaRoomService: MultimediaRoomService,
    private blackSimpleToasterService: BlackSimpleToasterService,
    private modalService: ModalService,
    private store: Store,
    private logService: LogService,
    private browserService: BrowserService,
    private websocketService: WebsocketService,
    private subscriptionService: SubscriptionService,
    private roomPresenceService: RoomPresenceService,
    private guestAuthService: GuestAuthService,
    public groupShareParticipantService: GroupShareScreenParticipantService,
    private entryFlowService: EntryFlowService,
    _roomLifecycleService: RoomLifecycleService,
    _roomStateService: RoomStateService,
    _cleanUpTasksService: CleanUpTasksService,
    private debuggerService: DebuggerService,
    public transitionService: TransitionService,
    public transitionPresentationService: TransitionPresentationService,
    public badgingService: BadgingService,
    private ldService: LaunchDarklyService,
    private groupShareScreenService: GroupShareScreenService,
    private ringColorsService: RingColorsService,
    private favoriteService: FavoriteService,
    private speechToTextService: SpeechToTextService,
    private closedCaptionsService: ClosedCaptionsService,
    private emoteService: EmoteService,
    private eventsService: EventsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('JoinSocialHomeComponent');
    // User state
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isGuest, user }) => {
        this.user = user;
        this.isGuest = isGuest;
      });

    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isJoined) => (this.inSocial = isJoined));

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(
        startWith({} as Social),
        pairwise(),
        filter(([prevRoom, room]) => notNil(room) && notNil(prevRoom)),
        takeUntil(this.destroyed$),
      )
      .subscribe(([prevRoom, room]) => {
        this.social = room;
        this.socialId = room?.socialId;

        /** 
         If the user is not the host and the room is open and the host has not started the room
         then the user should be able to join or start the room after being on waiting for host state
        */
        if (!this.isHost && room?.isOpen && !room?.anyoneCanStart && this.hasWaitedForHost()) {
          this.hasWaitedForHost.set(false);
          this.canJoinOrStart = true;
          this.onJoinSocial(room).catch((e) => {
            this.error('Failed to change room', e);
          });
        }

        if (room) {
          this.updateSocial(room)
            .then(() => {
              if (prevRoom && prevRoom.socialId !== room.socialId && this.inSocial) {
                this.changeRoom(room).catch((e) => {
                  this.error('Failed to change room', e);
                });
              }
            })
            .catch((e) => {
              this.error('Failed to update social', e);
            });
        }
      });

    // check to see if user is using customer api
    this.store
      .select(socialAppSelectors.customerAPIEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((api) => {
        this.customerAPIEnabled = api;
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => {
        this.isUserPresenting = isUserPresenting;
      });

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.isVideoEnabled = enabled;
      });

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.isMicEnabled = enabled;
      });

    this.store
      .select(socialAppSelectors.getRemoteScreenSharePresenter)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((remoteScreenSharePresenter) => {
        this.remoteScreenSharePresenter = remoteScreenSharePresenter;
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => {
        this.isHost = isHost;
      });

    combineLatest([
      this.store.select(socialAppSelectors.isPresentationActive),
      this.store.select(socialAppSelectors.presentationActive),
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
        takeUntil(this.destroyed$),
      )
      .subscribe(([isPresentationActive, presentationActive]) => {
        this.isPresentationActive = isPresentationActive;
        if (isPresentationActive && !this.isLoadingPresentation) {
          this.isLoadingPresentation = true;
          void delayFor(5000).then(() => {
            this.isLoadingPresentation = false;
          });
        }
        // TODO: deprecate this backup check and test
        // OLD: remove presentation from map - backup
        // if (!isPresentationActive && this.socialId && presentationActive[this.socialId]) {
        //   void SocialStateMachine.shared.requestRemoveActivePresentation({
        //     socialId: this.socialId ?? '',
        //   });
        // }
        if (!isPresentationActive && this.socialId && presentationActive[this.socialId]) {
          void delayFor(500).then(() => {
            this.sceneService?.sceneManager?.healthCheckAll();
          });
        }

        // restart player video after presentation ends
        const videoPlayer = this.sceneService.sceneManager?.player?.videoElement;
        videoPlayer?.play().catch((e) => this.error('Failed to play video', e));
        if (!isPresentationActive && videoPlayer && !videoPlayer.paused) {
          void this.playWhenReady(videoPlayer);
        }
      });

    this.store
      .select(socialAppSelectors.isBadgingModalOpen)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isBadgingModalOpen) => (this.isBadgingModalOpen = isBadgingModalOpen));

    this.store
      .select(socialAppSelectors.specialAccessGroupModal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ mode, open }) => {
        this.isSAGModalOpen = open;
        this.isSAGModalRequestAccess = mode === SpecialAccessGroupModalMode.REQUEST_ACCESS;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_android_fullscreen)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((useAndroidFullScreen) => (this.useAndroidFullScreen = useAndroidFullScreen));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_show_invite_modal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showInviteModal) => (this.showInviteModal = showInviteModal));

    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const recordSetting = this.sceneService.getSetting('RECORD') ?? false;
        this.isRoomSettingsRecording = !!recordSetting;
        if (this.isRoomSettingsRecording) {
          this.store.dispatch(actions.UPDATE_WIDE_RECORDING({ enabled: this.isRoomSettingsRecording }));
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.closed_captions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((closedCaptionsFlagEnabled) => {
        this.closedCaptionsFlagEnabled = closedCaptionsFlagEnabled;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.ai_summaries)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((aiSummaries) => {
        this.aiSummaries = aiSummaries;
      });

    this.store
      .select(selectors.getTransitionTimers)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((transitionTimers) => {
        this.backgroundFadeOutTimeout = transitionTimers.backgroundFadeOutTimeout;
      });

    this.soundControl.createAudioContext().catch((e) => {
      this.error('Failed to create audio context', e);
    });

    if (this.eventsService.eventId) {
      this.eventsService
        .getEvent(this.eventsService.eventId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((event) => {
          this.event = event;
        });
    }
  }

  public async ngOnInit(): Promise<void> {
    this.isBrowser = isPlatformBrowser(AppComponent.PLATFORM_ID);
    if (window['HubSpotConversations'] && window['HubSpotConversations'].widget) {
      window['HubSpotConversations'].widget.remove();
    }

    this.isMobileOrTablet = this.browserService.isMobileOrTablet;
    this.eventName = this.eventsService.eventName;

    // When initially joining social
    // Update LD user context
    this.pageLoaded = false;
    setTimeout(() => {
      this.pageLoaded = true;
    }, 300);

    await this.ldService.updateUserContext();

    const typedNav: Navigator = navigator;
    let wakeLock: WakeLockSentinel | null = null;

    // Only request wake lock when page is visible
    const requestWakeLock = async () => {
      if (!('wakeLock' in navigator)) {
        this.info('Wake Lock is not supported in this browser');
        return;
      }

      if (document.visibilityState !== 'visible') {
        this.info('Delaying Wake Lock request until page is visible');
        return;
      }

      try {
        wakeLock = await typedNav.wakeLock.request('screen');
        this.info('Wake Lock is active');
      } catch (err) {
        // Check for specific error types
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          this.error('Wake Lock request not allowed. Ensure page is visible and has user interaction', err);
        } else {
          this.error('Wake Lock request has failed', err);
        }
      }
    };

    // Initial request
    await requestWakeLock();

    // Re-request wake lock when visibility changes to visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !wakeLock) {
        requestWakeLock().catch((err) => {
          this.error('Failed to re-acquire wake lock on visibility change', err);
        });
      }
    });

    this.websocketService.peerCandidate$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((msg) => this.handlePeerCandidate(msg));

    this.websocketService.message$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((msg) => this.handleWebsocketMessage(msg));

    this.websocketService.disconnect$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((status) => this.handleWebsocketClose(status));

    this.renderer2.addClass(document.body.parentElement, 'socialBodyClass');
    this.renderer2.addClass(document.body, 'socialBodyClass');

    await this.updateSocial(this.social);

    this.store
      .select(selectors.getFullClusters)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const mainPlayer = this.sceneService.getPlayer();
        this.isPlayerOutsideGroupLimit = !!mainPlayer?.groupWarning;
      });

    // Leave Room and Close Room
    this.leaveRoomSubscription = this.roomPresenceService.leaveRoom$
      .pipe(
        switchMap((type) => {
          // The overlay should not remain up if the user is leaving the room for some reason.
          this.hideOverlay();
          // End transcription listening
          this.speechToTextService.finishTranscriptionListener();
          if (wakeLock) {
            wakeLock
              .release()
              .then(() => {
                wakeLock = null;
              })
              .catch((err) => {
                this.error('Wake Lock release has failed', err);
              });
          }
          switch (type) {
            case LeaveRoomType.LEAVE_JOIN_ANOTHER:
              return of(this.leaveRoom({ survey: true }));
            case LeaveRoomType.LEAVE_ROOM:
              return of(this.leaveRoom({ survey: true }));
            case LeaveRoomType.LEAVE_UNJOINED_ROOM:
              return of(this.leaveRoom({ survey: false }));
            case LeaveRoomType.LEAVE_AND_CLOSE_ROOM:
              this.leftSocialOnPurpose = true;
              return of(this.leaveRoom({ survey: true }));
            case LeaveRoomType.LEAVE_BANNED_USER:
              return of(this.leaveRoom({ reloading: true, bannedUserLeaving: true, survey: false }));
            case LeaveRoomType.CLOSE_WINDOW:
              return of(this.leaveRoom({ reloading: true, survey: false }));
            default:
              assertNever(type);
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    // Group Share
    combineLatest([
      this.transitionService.isTransitioning$,
      this.transitionPresentationService.isTransitioning$,
      this.store.select(socialAppSelectors.getMyGroupScreenShare),
      this.groupShareScreenService.isGroupShareActiveOrAvailable$,
    ])
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(
        ([isTransitioning, isPresentationTransitioning, myGroupScreenShare, isGroupShareActiveOrAvailable]) => {
          this.isTransitioning = isTransitioning || isPresentationTransitioning;
          // should only end share intentionally
          if (this.groupParticipantScreenShareActive && !myGroupScreenShare && !isGroupShareActiveOrAvailable) {
            this.transitionService
              .enableFadeInBackground()
              .then(() => {
                this.groupParticipantScreenShareActive = false;
              })
              .catch((err) => {
                this.error('Failed to execute transition', err);
              });
            // v0 should end share if user is not in group
          } else if (!myGroupScreenShare) {
            this.groupParticipantScreenShareActive = isGroupShareActiveOrAvailable;
          } else if (myGroupScreenShare && !isGroupShareActiveOrAvailable) {
            void delayFor(100).then(() => {
              // play video element
              const videoPlayer = this.sceneService.sceneManager?.player?.videoElement;
              if (videoPlayer) {
                this.playWhenReady(videoPlayer);
              }
            });
          }
        },
      );

    this.store
      .select(socialAppSelectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserSharing) => {
        this.isUserGroupSharing = isUserSharing;
      });

    this.performerViewService.isPerforming.pipe(takeUntil(this.destroyed$)).subscribe((res) => {
      this.isPerforming = res;
      const sceneManager = this.sceneService.sceneManager;
      if (!sceneManager) {
        return;
      }

      // Performance Ends
      if (res.valueOf() === false) {
        // const participants = sceneManager.participants;
        // const videoElement = sceneManager && sceneManager.player && sceneManager.player.videoElement;
        // if (videoElement) {
        //   videoElement.pause();
        // }

        // participants.forEach((p) => {
        //   const ve = p.videoElement;
        //   if (ve) {
        //     ve.pause();
        //   }
        // });

        // Update Participants Sound by distance
        this.multimediaRoomService.setAllVideoParticipantsSound();
      }
    });

    // Subscribe to SomeoneIsPerforming
    this.performerService.isPerforming$.pipe(takeUntil(this.destroyed$)).subscribe((newValue) => {
      if (newValue === null) {
        return;
      }

      // Someones Performance Ends
      if (this.someoneIsPerforming === true && newValue.valueOf() === false) {
        // keep value
        this.someoneIsPerforming = newValue;

        // Update Participants Sound by distance
        this.multimediaRoomService.setAllVideoParticipantsSound();
        // Someones Performance Starts
      } else if (this.someoneIsPerforming === false && newValue.valueOf() === true) {
        this.someoneIsPerforming = newValue;
      }
    });

    this.videoRoomService.socialEnded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((reason) => this.handleSocialEnded(reason));

    this.debuggerService.disableCaptureMedia.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (result: boolean) => {
        this.disableCaptureDebugData = result;
      },
    });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_debugger)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((allowDebugger) => (this.allowDebugger = allowDebugger));

    // TODO: should this be here?
    this.ringColorsService.ringColorsStream$.pipe(takeUntil(this.destroyed$)).subscribe((resp) => {
      this.store.dispatch(actions.VISUAL_INDICATOR({ visualIndicatorInfo: resp }));
    });

    this.sceneService.sceneManager?.sagMap$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.multimediaRoomService.setAllVideoParticipantsSound());

    // Handle waiting for host step changes
    this.roomPresenceService.waitingForHostStep$.pipe(takeUntil(this.destroyed$)).subscribe(async (step) => {
      this.waitingForHostStep.set(step);
      this.changeDetectorRef.markForCheck();
      if (step) {
        await this.hideTransitionOverlay(500);
      }
    });
  }

  private playWhenReady(videoPlayer: HTMLVideoElement): void {
    if (videoPlayer.readyState >= 2) {
      videoPlayer.play().catch((e) => this.error('Failed to play video', e));
    } else {
      videoPlayer.oncanplay = () => {
        videoPlayer.play().catch((e) => this.error('Failed to play video', e));
      };
    }
  }

  /**
   * Handle social end
   * @param reason The reason why the social ended
   */
  private handleSocialEnded = async (reason: SocialEndType): Promise<void> => {
    this.info('Social has ended', { reason });
    this.leftSocialOnPurpose = true;
    const { socialId } = this.social ?? {};

    if (this.inSocial && socialId && reason !== SocialEndType.HOST) {
      try {
        await firstValueFrom(this.videoChatService.leaveSocial(socialId));
      } catch (e) {
        this.error('Failed to gracefully leave social', e);
      }
    }

    if (reason === SocialEndType.HOST || reason === SocialEndType.TIME) {
      await this.indicateRoomEnd(reason, this.inSocial);
      return;
    }

    this.leftSocialOnPurpose = false;
    this.showLoader = false;

    if (this.leftSocialOnPurpose && this.social) {
      const route = this.getSurveyRoute();
      await this.router.navigate([route]);
    } else {
      location.reload();
    }
  };

  private getSurveyRoute(): string {
    const { socialId } = this.social ?? {};

    if (!socialId) {
      return '';
    }

    if (this.aiSummaries) {
      return `web/admin/activate-survey/${socialId}`;
    }

    return this.isGuest
      ? `home/scootaverses/guest/activate-survey/${socialId}`
      : `home/scootaverses/activate-survey/${socialId}`;
  }

  @HostListener('window:beforeunload')
  public async windowUnload() {
    this.roomPresenceService.closeWindow();
    await this._destroy();
  }

  public async ngOnDestroy(): Promise<void> {
    await cleanAllStreams();
    this.store.dispatch(actions.LEAVE_SOCIAL());
    // this is called elsewhere, but we need to make sure it's called here too
    if (this.deviceService.cameras.length || this.deviceService.mics.length) {
      await this.deviceService.destroy();
    }
    this.participantConnectedSubscription?.unsubscribe();
    this.websocketOpenSubscription?.unsubscribe();
    this.clearAdmissionInterval();
    this.destroyed$.next(true);
    this.destroyed$.complete();
    await this._destroy();
  }

  private async _destroy(): Promise<void> {
    void this.shareScreenParticipantService.clear();
    await this.shareScreenService.stopSharing();

    this.trace('ngOnDestroy() called for Join Social Home Component');
    this.didDisconnect = true;

    this.renderer2.removeClass(document.body.parentElement, 'socialBodyClass');
    this.renderer2.removeClass(document.body, 'socialBodyClass');
    this.renderer2.removeClass(document.body.parentElement, 'socialBodyClassNoOverflow');
    this.renderer2.removeClass(document.body, 'socialBodyClassNoOverflow');

    // remove theme CSS Variables
    const themeVars = [
      '--primary-color',
      '--secondary-color',
      '--button-text-color',
      '--button-background-color',
      '--background-color',
      '--background-gradient',
    ];
    for (const key of themeVars) {
      document.body.style.removeProperty(key);
      document.body.parentElement?.style.removeProperty(key);
    }

    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
    }
    this.overlayTimeout = undefined;
    window.removeEventListener('unhandledrejection', this.unhandledRejection.bind(this));

    await this.soundControl.destroy();
  }

  @HostListener('window:unhandledrejection', ['$event'])
  public unhandledRejection(event: PromiseRejectionEvent): void {
    this.error('Unhandled rejection', event?.reason ?? 'unknown reason');
  }

  public get sideContainerType(): typeof SideContainerType {
    return SideContainerType;
  }

  /**
   * Determine if the Presentation should be displayed
   */

  public get displayPresentation(): boolean {
    return (
      // check if presentation is active
      (this.isPresentationActive ||
        // group share participant?
        (this.groupParticipantScreenShareActive && this.groupShareParticipantService.userHasJoinedShare) ||
        // group share presenter?
        this.isUserGroupSharing) &&
      // not transitioning
      !this.isTransitioning
    );
  }

  /**
   * Determine if the zoom controls should be displayed
   */
  public displayZoomControls(): boolean {
    return !this.displayPresentation;
  }

  public showCaptionsMingleMode(): boolean {
    return this.closedCaptionsFlagEnabled && this.closedCaptionsEnabled && !this.displayPresentation;
  }

  private async updateSocial(social?: Social): Promise<void> {
    if (!social || !this.user) {
      return;
    }
    if (!this.social?.isValid) {
      await this.indicateRoomEnd(SocialEndType.EMPTY, this.inSocial);
      return;
    }
    this.logService.setContextAttribute('socialId', social.socialId ?? 'invalid');
    this.social = social;
    this.socialId = social.socialId;

    this.videoRoomService.setSocial(this.social);

    if (!this.isGuest && (!this.user.firstName || !this.user.lastName)) {
      await this.showNameModal();
    }

    this.checkIfCanJoinSocial();
    this.isSocialLoaded = true;

    // Get company for user so we have name to show in header
    void this.companyController
      .getUserCompany(social.userId ?? '')
      .then((company) => {
        if (company) {
          this.company = company;
        }
      })
      .finally(() => (this.showLoader = false));

    this.canJoinOrStart = this.social.isOpen || this.canStartSocial;
  }

  private checkIfCanJoinSocial(): void {
    this.videoChatService
      .canJoinSocial(this.socialId ?? '')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((canJoin) => {
        if (!canJoin.valueOf()) {
          // User not permitted to join
          this.showNoAccessError();
        }
      });
  }

  private async clearRoom(): Promise<void> {
    this.leftSocialOnPurpose = true;
    this.didDisconnect = true;

    await this.websocketService.close();

    // Close room messages
    this.headerToastMessageService.removeAllMessages();
    this.blackSimpleToasterService.close();
    this.modalSocialEventService.close();
    this.modalService.close();

    await this.cleanSocialEvents();

    this.websocketOpenSubscription?.unsubscribe();
    this.websocketOpenSubscription = undefined;

    if (this.videoRoomService.participants) {
      this.videoRoomService.clear();
    }

    this.subscriptionService.clear();
    this.videoRoomService.removeParticipants();
    this.sceneService.sceneManager?.clear();

    SocialStateMachine.shared.reset();
  }

  private async cleanSocialEvents(): Promise<void> {
    // Clean Events in Progress
    if (this.toastService.isUserToaster && this.toastService.isToastHappening) {
      await this.toastService.requestEndToast(true).catch((e) => this.error('Error ending toast', e));
    }

    if (this.convoService.isStarting || this.convoService.isInProgress) {
      this.convoService.requestEndConvo().catch((e) => this.error('Error ending convo', e));
    }

    // Handle end Performance if performer disconnects
    const { state } = SocialStateMachine.shared;
    if (state?.eventType === SocialEventType.PRESENTATION && this.user) {
      assertDefined(this.user.userId);
      if (SocialStateHelper.isUserPresenter(state, this.user.userId)) {
        await this.presentationService.removeActivePresenter(this.user.userId);
      } else if (SocialStateHelper.isUserInvited(state, this.user.userId)) {
        await this.presentationService.removeInvitedPresenter(this.user.userId);
      }
    }

    void this.shareScreenParticipantService.clear();
    await this.shareScreenService.stopSharing();

    if (this.someoneIsPerforming) {
      this.someoneIsPerforming = false;
    }
  }

  private async leaveRoom({
    bannedUserLeaving,
    reloading,
    survey,
  }: {
    reloading?: boolean;
    bannedUserLeaving?: boolean;
    survey?: boolean;
  } = {}): Promise<void> {
    // Clean Events in Progress before leaving room
    if (this.event) {
      this.eventsService.registrationId = undefined;
      this.eventsService.eventName = undefined;
      this.eventsService.eventId = undefined;
    }
    await this.shareScreenService.stopSharing();
    await this.groupShareScreenService.stopSharing(this.user?.userId as string, true);
    this.sceneService.sceneManager?.clear();

    const customerAPIEnabled = this.customerAPIEnabled;
    this.store.dispatch(actions.LEAVE_SOCIAL());

    this.logService.setContextAttribute('socialId', null);

    if (this.browserService.isAndroidDevice()) {
      this.renderer2.removeClass(document.body.parentElement, 'social-android-tablet-body');
      this.renderer2.removeClass(document.body, 'social-android-tablet-body');

      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen?.();
        } catch (e) {
          this.error('Failed to exit fullscreen', e);
        }
      }
    }

    this.peerConnection?.close();
    this.peerConnection = null;

    this.leaveRoomSubscription?.unsubscribe();

    this.websocketOpenSubscription?.unsubscribe();
    this.websocketOpenSubscription = undefined;

    this.clearAdmissionInterval();

    this.sideSectionService.sideContainerType = SideContainerType.NONE;

    this.leftSocialOnPurpose = true;
    this.didDisconnect = true;

    // Close room messages
    this.headerToastMessageService.removeAllMessages();
    this.blackSimpleToasterService.close();
    this.modalSocialEventService.close();
    this.modalService.close();

    this.sideSectionService.sideContainerType = SideContainerType.NONE;

    await this.cleanSocialEvents();
    const { state } = SocialStateMachine.shared;
    if (state?.readOnly.waitingUsers) {
      if (SocialStateHelper.isWaitingUser(Object.keys(state.readOnly.waitingUsers), this.user?.userId ?? '')) {
        await this.videoChatService.setAdmittedStatus(
          this.social?.socialId ?? '',
          this.user?.userId ?? '',
          false,
          false,
        );
      }
    }

    // close performer if someone Is Performing
    if (this.someoneIsPerforming) {
      this.someoneIsPerforming = false;
    }

    this.headerMultipleMessageService.removeMessage();

    const socialId = this.social?.socialId ?? '';

    if (socialId && !reloading) {
      if (customerAPIEnabled) {
        await this.guestAuthService.handleLeaveRoom();
        return;
      }

      if (survey) {
        const route = this.getSurveyRoute();
        await this.router.navigate([route]);
      } else {
        await this.router.navigate(['/']);
      }
    } else if (bannedUserLeaving) {
      await this.indicateRoomEnd(SocialEndType.EMPTY, false);
    }

    SocialStateMachine.shared.reset();
  }

  protected async onJoinSocial(_social: Social): Promise<void> {
    this.showLoader = true;

    if (!this.hasWaitedForHost()) {
      void this.showTransitionOverlay();
    }

    // If they have waited for the host, then they should not be able to join or start
    if (!this.canJoinOrStart) {
      this.waitingForHostStep.set(undefined);
      this.hasWaitedForHost.set(true);
      return;
    }

    let social = { ..._social };
    if (!social.socialMeta?.peerConnectionURL) {
      social = await firstValueFrom(this.videoChatService.getSocial(social.socialId ?? '', true));
    }

    // Note: if they sat in waiting room a while then this might be stale meta and not accurately
    // reflect the count in the social.  We need to rely on limits
    // Add this so not overflow.
    this.clearAdmissionInterval();

    this.renderer2.addClass(document.body.parentElement, 'socialBodyClassNoOverflow');
    this.renderer2.addClass(document.body, 'socialBodyClassNoOverflow');

    if (this.browserService.isAndroidDevice()) {
      if (this.useAndroidFullScreen) {
        try {
          await document.body.requestFullscreen?.();
        } catch (e) {
          this.error('Failed to request fullscreen permission', e);
        }
      }

      this.renderer2.addClass(document.body.parentElement, 'social-android-tablet-body');
      this.renderer2.addClass(document.body, 'social-android-tablet-body');
    }

    const { socialId, userId: hostId } = social;
    const { userId } = this.user ?? {};

    if (!socialId || !hostId) {
      this.fatal('Got invalid social, could not determine socialId or hostId', social);
      return;
    }

    if (!userId) {
      this.fatal('Could not determine userId when opening social');
      return;
    }

    this.canJoinOrStart = false; // set this so button changes immediately - turn on after they join
    if (social.isOpen) {
      this.openPeerConnection();
      this.onSocialChanged(socialId);

      this.store.dispatch(actions.JOIN_SOCIAL({ social }));

      this.roomPresenceService.joinRoom();

      return;
    }

    if (!this.canStartSocial) {
      this.error('Attempted to open a room when not host or anyone can start', { social });
      return;
    }

    // Determine the current region based on latency, then start the room requesting that region
    from(this.videoChatService.getRegion())
      .pipe(
        catchError(() => {
          return of(DEFAULT_REGION);
        }),
        switchMap((region) => this.videoChatService.startSocial(socialId, region)),
        tap((newSocial) => (this.social = newSocial)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: async (newSocial) => {
          this.store.dispatch(actions.START_SOCIAL({ social: newSocial }));

          const peerConnectionURL = newSocial?.socialMeta?.peerConnectionURL;
          if (peerConnectionURL && peerConnectionURL !== this.websocketService.url) {
            this.info('Websocket URL has changed, closing old connection');
            await this.websocketService.close();
            this.websocketService.connect(peerConnectionURL);
          }

          await this.delayedShowInviteDialog();

          this.openPeerConnection();
          this.onSocialChanged(socialId);

          this.roomPresenceService.joinRoom();
        },
        error: (err: unknown) => {
          this.handleStartSocialFailed({ e: err, isHost: hostId === userId });
        },
      });

    this.sceneService.isLoading = false;

    //Set some additional time of loading for the cases where it should enter a presentation directly
    setTimeout(() => {
      this.showLoader = false;
    }, this.sceneService.isLoadingRoomTimeout);
  }

  private get canStartSocial(): boolean {
    return this.isHost || !!this.social?.anyoneCanStart;
  }

  private handleStartSocialFailed({ e, isHost }: { e: unknown; isHost: boolean }): void {
    this.hideOverlay();

    const { error, status } = e as HttpErrorResponse;

    if (status === 403 && (error.includes(SOCIAL_LICENSE_EXCEEDED) || error.includes(SOCIAL_LICENSE_REQUIRED))) {
      let numOpenRooms = 0;
      const splitStatusArr = error.split(' ');

      if (error.includes(SOCIAL_LICENSE_REQUIRED)) {
        // "User ca03ee52-5864-48bb-8c1f-b61d37bff84d does not have a license required for 2 open rooms"
        numOpenRooms = parseInt(splitStatusArr[9], 10) - 1;
      } else if (error.includes(SOCIAL_LICENSE_EXCEEDED)) {
        // "User 529af387-50bb-43ff-8f89-f0fee8b75b92 exceeds license limit of 1 for 2 open rooms"
        numOpenRooms = parseInt(splitStatusArr[6], 10);
      }

      this.warn('User does not have enough licenses to start a room', { numOpenRooms, isHost });

      this.showNotEnoughLicensesError(numOpenRooms, isHost);
      return;
    }

    this.error('Failed to start room', e);

    this.showNoServiceError();
  }

  public onHostAdmissionRequired(social: Social): void {
    const { socialId } = social;
    if (!socialId) {
      this.error('onHostAdmissionRequired got social with invalid socialId');
      return;
    }

    this.showOverlay('Waiting for the host to let you in...');
    this.admissionIntervalTimeout = setInterval(async () => {
      try {
        const res = await this.videoChatService.checkAdmittedStatus(socialId, true);
        if (!res) {
          return;
        }
        if (res.banned) {
          this.roomPresenceService.handleBannedUser();
        }
        if (res.admitted) {
          await this.onJoinSocial(social);
        }
      } catch (err) {
        /** if the room has been closed for all */
        const httpErr = err as HttpErrorResponse;
        if (httpErr.status.toString() === '404') {
          this.clearAdmissionInterval();
          this.hideOverlay();
        }
      }
    }, 2000);
  }

  public showInviteDialog(): void {
    if (!this.showInviteModal) {
      return;
    }
    const data: ModalInviteData = {
      socialURI: this.social?.socialURI ?? '',
      text: 'Paste the link to your calendar event or share directly with others.',
      modalTitle: 'You’re all by yourself! Invite others to this Room',
      firstName: this.user?.firstName ?? '',
      lastName: this.user?.lastName ?? '',
      inSocial: true,
    };
    this.dialog.open(ModalSocialInviteComponent, {
      panelClass: 'no-padding-social-modal',
      backdropClass: 'social-page-backdrop',
      data,
    });
  }

  private handleWebsocketClose({ error, type }: DisconnectStatus) {
    this.info(`Audio Web Socket closed -- User left on purpose: ${this.didDisconnect}`);

    if (type === WebsocketDisconnectType.INCOMPATIBLE_CLIENT) {
      const data = {
        modalTitle: 'Refresh Required',
        text: 'Preciate has been updated. A refresh is required to join this room.',
        primaryButtonText: 'Okay',
      };

      const dialogRef = this.dialog.open(ModalSimpleSocialComponent, {
        panelClass: 'social-modal',
        data: data,
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          location.reload();
        });
      return;
    }

    if (error) {
      this.retryPeerConnection();
      this.error(`Error closing websocket -- websocket had an abnormal closure.`);

      if (!this.didDisconnect) {
        this.websocketTimerService.startTrackingReconnectAttempts();
      }
    }
  }

  private handleWebsocketState(): void {
    if (!this.user) {
      return;
    }
    assertDefined(this.user.userId);
    // FIXME: I suspect this can be replaced with moving host handling directly into the host service based on shared
    // state change. It does not belong here.
    /** add main host to the hosts array, else  verify if the user is a new host */
    if (this.social?.userId === this.user.userId && !this.isHost) {
      this.hostService.addHost(this.user.userId);
    }
    return;
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;

    switch (type) {
      case IncomingMessageType.MOVED: {
        this.multimediaRoomService.setAllVideoParticipantsSound();
        return;
      }

      case IncomingMessageType.STATE_CHANGE:
        this.handleWebsocketState();
        return;

      case IncomingMessageType.INIT_STATE:
        this.handleWebsocketState();
        return;

      case IncomingMessageType.STARTING_POINT_FULL: {
        this.entryFlowService.handleStartingPointAtMax();
        return;
      }

      // TODO: Move this to ring-colors.service.ts
      case IncomingMessageType.USER: {
        if (msg.user && msg.user.userId && msg.user.ringColor) {
          // get user and update color
          this.sceneService.sceneManager?.updateRingColors(msg.user.userId, msg.user.ringColor);
        }
        return;
      }

      case IncomingMessageType.INITPOS: {
        this.myX = msg.x;
        this.myY = msg.y;
      }
    }
  };

  private handlePeerCandidate = async (msg: PeerCandidate): Promise<void> => {
    if (!this.peerConnection) {
      this.warn('Got peer connection candidate while peer connection is not set');
      return;
    }
    const candidate = new RTCIceCandidate(msg);
    try {
      this.info('Adding ice candidate', candidate);
      await this.peerConnection.addIceCandidate(candidate);
    } catch (e) {
      this.error('Error adding ice candidate', e);
    }
  };

  private openPeerConnection(): void {
    const meta = this.social?.socialMeta;

    if (meta?.peerConnectionURL) {
      this.info('Opening Peer Connection and Web Sockets with url: ', meta.peerConnectionURL);
    }

    this.peerConnection?.close();

    const { peerConnectionURL } = meta ?? {};

    if (!peerConnectionURL) {
      this.fatal('Did not receive url in social meta', { social: this.social });
      return;
    }

    if (
      this.websocketService.state === WebsocketStateType.DISCONNECTED ||
      this.websocketService.state === WebsocketStateType.FAILED
    ) {
      this.info('Websocket is not connected, connecting...');
      this.websocketService.connect(peerConnectionURL);
    }
  }

  private retryPeerConnection(): void {
    if (this.didDisconnect) {
      return;
    }
    this.peerConnection = null;

    setTimeout(() => {
      this.info('Retrying to open peer connection');
      this.openPeerConnection();
    }, 2000);
  }

  private initListeners(): void {
    this.videoRoomService.didAddParticipantVideo
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((rp: RoomParticipant) => {
        this.ngZone.runOutsideAngular(() => {
          const player = this.sceneService?.sceneManager?.getParticipant(rp.userId);
          if (player) {
            // handle video did sub for player
            if (rp.videoElement) {
              void player.setVideoElement(rp.videoElement);
            }

            if (this.isPerforming && this.performerViewService.audience) {
              this.performerViewService.setAudience();
              this.performerViewService.refreshUserVideo(rp.userId);
            }
          }
        });
      });

    // Unsubscribe from Connected participant prior to subscribing
    this.participantConnectedSubscription?.unsubscribe();
    this.participantConnectedSubscription = this.videoRoomService.participantConnected
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((rp: RoomParticipant) => {
        this.sceneService.sceneManager?.runIfPixiInitialized(() => {
          const x = rp.startX;
          const y = rp.startY;
          const player = this.addParticipant(rp, x, y);
          if (player) {
            rp.unsubscribeListener = player;
            if (this.sceneService.sceneManager) {
              player.setDisplayPosition(x, y, this.sceneService.sceneManager.bounds);
            }
            player.setInitialAudio(rp.initialMicEnabled);
            player.enableRemoteVideo(rp.initialVideoEnabled);
          } else {
            this.error('Failed to add participant', rp);
          }

          this.participantJoined(rp.userId);
        });
      });

    this.videoRoomService.participantDisconnected
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((sp: RoomParticipant) => {
        this.participantLeft(sp.userId);
      });

    this.videoRoomService.participantVideoStatusChanged
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe(({ enabled, userId }: { enabled: boolean; userId: string }) => {
        this.multimediaUserService.setParticipantVideo(userId, enabled);
      });

    this.videoRoomService.participantAudioStatusChanged
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe(({ enabled, userId }: { enabled: boolean; userId: string }) => {
        this.multimediaUserService.setParticipantAudio(userId, enabled);
      });

    this.videoRoomService.didAddParticipantAudio
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((sp: RoomParticipant) => {
        this.ngZone.runOutsideAngular(() => {
          if (sp.audioElement) {
            this.addParticipantSoundElementToSocial(sp.audioElement, sp.userId);
          }
        });
      });

    this.videoRoomService.didSubscribeParticipant
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe(({ userId }: PreciateEventHelper) => {
        if (!userId) {
          return;
        }
        this.sceneService.sceneManager?.setParticipantVideoVisibility(userId, true);
      });

    this.videoRoomService.didUnsubscribeParticipant
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((sp: PreciateEventHelper) => {
        this.sceneService.sceneManager?.setParticipantVideoVisibility(sp.userId, false);
      });

    return;
  }

  private handleSocialJoined(socialAttendee: SocialAttendee): void {
    this.websocketOpenSubscription = this.websocketService.open$
      .pipe(
        filter((open) => open && this.inSocial),
        tap(() => {
          this.info('WS connected on join-social-home');
          if (this.websocketTimerService.didAttemptToReconnect) {
            this.websocketTimerService.didReconnect();
          }
        }),
        switchMap(() => this.websocketService.joinRoom(socialAttendee.ringColor)),
        catchError((e: unknown) => {
          this.fatal('Error joining room', e);
          this.showError(
            'The room could not be joined',
            'The room is currently unavailable. If the problem persists, please contact support.',
          )
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.router.navigate(['/']));
          return of(false);
        }),
        switchMap(() => this.videoRoomService.createRoom()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (room) => {
          if (!room) {
            this.error('Failed to create room on join social');
            return;
          }
          this.info('Main Participant Joined Social ', socialAttendee);

          // Add My Player and Participant
          if (this.videoRoomService.publisherVideo) {
            this.sceneService?.sceneManager?.runIfPixiInitialized(async () => {
              if (!this.videoRoomService.publisherVideo) {
                this.error('Publisher video is not set', socialAttendee);
                return;
              }
              await this.addMyPlayer(this.videoRoomService.publisherVideo, socialAttendee.ringColor);
            });
          }
          if (this.videoRoomService.publisherAudio) {
            this.addParticipantSoundElementToSocial(
              this.videoRoomService.publisherAudio,
              this.user?.userId ?? '',
              true,
            );
          }
          this.canJoinOrStart = true;
          this.showLoader = false;
          this.sceneService.isLoading = false;

          // Just to prevent hide overlay when it shouldn't
          if (this.isHost || this.social?.isOpen || this.social?.anyoneCanStart) {
            void this.hideTransitionOverlay();
          }

          this.favoriteService.setFavoriteList(socialAttendee.favoriteUserIds || []);

          // Initialize captions listeners
          if (this.closedCaptionsFlagEnabled) {
            // Receive captions listener
            this.closedCaptionsService.initializeClosedCaptionsListener();
            this.captionsListenersInitialized = true;
            if (this.speechToTextService.browserSupport) {
              // Broadcast captions listener
              this.speechToTextService.initializeSpeechListener();
            }
          }

          this.requestPreviousChatMessages();
        },

        error: (error: unknown) => {
          // Something is really broken if after 30 seconds we don't get initial pos, reload.
          this.error(`Initial position message wasn't received, reloading tab. - ${error}`);
          location.reload();
        },
      });
  }

  private onSocialChanged(socialId: string): void {
    if (!socialId) {
      this.error('Invalid socialID');
      return;
    }
    this.showLoader = true;
    this.sceneService.isLoading = true;
    this.sceneService?.sceneManager?.runIfPixiInitialized(() => {
      this.initListeners();
      this.videoChatService
        .joinSocial(socialId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (attendee) => this.handleSocialJoined(attendee),
          error: async (error: unknown) => {
            this.sceneService.isLoading = false;
            // Hide overlay if error occurs during join social
            this.hideOverlay();
            const httpError = error as HttpErrorResponse;
            if (httpError.status.toString() === '403') {
              this.error('Scoot responded with 403 for Join Now', error);
              this.showSocialHasReachedLimitError();
              return;
            }
            this.error('Scoot responded for Join Now with error: ', error);
            await this.indicateRoomEnd(SocialEndType.EMPTY, false);
          },
        });
    });
  }

  public get shouldShowBadge(): boolean {
    return this.badgingService.smartBadgingEnabled && this.badgingService.isBadgeOpen;
  }

  private showNoServiceError(): void {
    const data = {
      modalTitle: 'Room Temporarily Unavailable',
      text: 'Your room is unable to open at the moment. Please try again in a few minutes.',
      primaryButtonText: 'Okay',
    };

    const dialogRef = this.dialog.open(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap(() => this.router.navigate([''])),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  public showNotEnoughLicensesError(numOpenRooms: number, isHost: boolean): void {
    let data: ModalDialogData = {
      title: "Host's Open Room Limit Reached",
      text: `You can not open this room because this host has the maximum number of rooms open.`,
      primaryButtonText: 'Okay',
    };

    if (isHost) {
      data = {
        title: 'Open Room Limit Reached',
        text: `You can not open this room because you have the maximum number of rooms (${numOpenRooms}) open under your current license. Close open rooms or purchase more licenses using the button below.`,
        primaryButtonText: 'Purchase more licenses',
        secondaryButtonText: 'Cancel',
      };
    }

    this.dialog
      .open<ModalDialogComponent, ModalDialogData, ModalDialogResult>(ModalDialogComponent, { data, width: '540px' })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        const destinationUrl = isHost && result === ModalDialogResult.PRIMARY ? 'https://scoot.app/pricing/' : '';
        this.router.navigate([destinationUrl]).catch((e) => {
          this.error('Failed to navigate to pricing page', e);
        });
      });
  }

  private showNoAccessError(): void {
    const data = {
      modalTitle: 'Social Private',
      primaryButtonText: 'Okay',
    };

    const dialogRef = this.dialog.open<ModalAccessComponent, ModalAccessData>(ModalAccessComponent, {
      width: '540px',
      panelClass: 'no-padding-social-modal',
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((_result) => {
        this.showLoader = false;
        this.router.navigate(['']).catch((e) => {
          this.error('Failed to navigate to home page', e);
        });
      });
  }

  private showError(title: string, text: string): Observable<void> {
    const data = {
      title,
      text: text,
    };

    return this.dialog
      .open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
        data,
      })
      .afterClosed();
  }

  private showNameModal(): Promise<unknown> {
    if (!this.user) {
      this.error('Cannot show name modal, user is undefined');
      return Promise.resolve();
    }

    const data = {
      modalTitle: "What's your name?",
      user: this.user,
    };

    const dialogRef = this.dialog.open<ModalNameComponent, ModalNameData>(ModalNameComponent, {
      data: data,
      width: '350px',
      panelClass: 'no-padding-social-modal',
    });

    return dialogRef.afterClosed().toPromise();
  }

  /**
   * Indicate to the user that the room has ended and  navigate to the home page.
   * @param reason The reason why the social has ended.
   * @param survey Show the user a survey
   */
  private async indicateRoomEnd(reason: SocialEndType, survey = false): Promise<void> {
    let data: ModalDialogData = {
      title: 'Room Closed',
      text: 'Sorry but this room has already been closed. Please create a new room to join.',
      primaryButtonText: 'Okay',
    };

    switch (reason) {
      case SocialEndType.HOST:
        if (this.isHost) {
          return;
        }
        data = {
          ...data,
          title: 'Room Closed By Host',
          text: 'The room was closed by the host of the meeting.',
          primaryButtonText: 'Dismiss',
        };
        break;

      case SocialEndType.TIME:
        data = this.social?.free
          ? {
              ...data,
              title: 'Out of Time',
              text: "You've reached the limit of 25 participants for 40 minutes on the free tier. To host more participants or extend the duration, consider upgrading to a premium plan..",
              primaryButtonText: 'Dismiss',
            }
          : {
              ...data,
              title: 'Meeting Ended',
              text: 'The room was closed because the meeting had a set end time.',
              primaryButtonText: 'Dismiss',
            };
        break;
      case SocialEndType.EMPTY:
        break;

      default:
        assertNever(reason);
    }

    const route = survey ? this.getSurveyRoute() : '';
    await this.router.navigate([route]);

    setTimeout(() => {
      this.dialog.open<ModalDialogComponent, ModalDialogData>(ModalDialogComponent, { data, width: '619px' });
    }, 1800);
  }

  private showSocialHasReachedLimitError(): void {
    const data = {
      modalTitle: 'Room has reached limit',
      text: `Sorry but this room has reached the max capacity.
          Try again in a while to see if space has freed up.`,
      primaryButtonText: 'Okay',
    };

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((_result) => {
        this.router.navigate(['']).catch((e) => {
          this.error('Failed to navigate to home page', e);
        });
      });
  }

  private participantJoined(_userId: string): void {
    const sceneManager = this.sceneService && this.sceneService.sceneManager;

    // If we are presenting and someone else joins, hide their bubble.
    if (this.isUserPresenting && sceneManager) {
      sceneManager.hideParticipants();
    }
  }

  private participantLeft(userId: string): void {
    // If I'm performing, remove from my audience
    if (this.isPerforming) {
      this.performerViewService.removeParticipant(userId);
    }

    // Count of participants
    this.removeParticipantFromWorld(userId);
  }

  private showOverlay(text: string, timeout = 0) {
    this.overlayText = text;
    this.showLoadingOverlay = true;

    if (timeout === 0) {
      return;
    }

    this.overlayTimeout = setTimeout(() => {
      this.showLoadingOverlay = false;
    }, timeout);
  }

  private hideOverlay() {
    this.showLoadingOverlay = false;
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
    }
  }

  private async changeRoom({ name, socialId }: Social): Promise<void> {
    assertDefined(socialId);
    // Logger and loading indicators
    this.info(`Switching to ${name} - Room ID: ${socialId}`);
    this.showOverlay(`You are now entering: ${name}`);

    // Clear previous room
    await this.clearRoom();

    // Load new social selected
    this.openPeerConnection();
    this.onSocialChanged(socialId);
  }

  private async addMyPlayer(videoElement: HTMLVideoElement, ringColor?: string): Promise<void> {
    if (this.user && this.social && this.sceneService.sceneManager) {
      const { theme } = this.social;
      if (!theme) {
        this.error('Theme is undefined, cannot construct player');
        return;
      }
      const player = new Player({
        store: this.store,
        renderer: this.sceneService.sceneManager.app!.renderer as unknown as Renderer,
        user: this.user,
        ringColor: ringColor,
        isPrimary: true,
        browserService: this.browserService,
        chatService: this.chatService,
        badgingService: this.badgingService,
        rtcStatsService: this.rtcStatsService,
        videoElement,
        x: this.myX,
        y: this.myY,
        videoEnabled: this.isVideoEnabled,
        emoteService: this.emoteService,
        sprites: this.sceneService.sceneManager.sprites,
      });
      this.attachPlayerListeners(player);

      if (this.sceneService.sceneManager) {
        this.sceneService.sceneManager.addPlayer(player);
        this.multimediaRoomService.setAllVideoParticipantsSound(player);
        this.sceneService.sceneManager.handleCustomerAPISettings();
      }
      await this.deviceService.initSoundAnalyser();
    }
  }

  private addParticipant({ user }: { user: BaseUser }, x?: number, y?: number): Player | undefined {
    const { firstName } = user;
    const { sceneManager } = this.sceneService;
    if (!sceneManager) {
      this.error('Scene manager is undefined, cannot add participant', { user });
      return;
    }

    if (!this.social) {
      this.error('Social is undefined, cannot add participant', { user });
      return;
    }

    if (!firstName) {
      this.error('Malformed user, cannot add', { user, social: this.social });
      return;
    }

    const participant = sceneManager.participants.get(user.userId);
    if (participant) {
      participant.user = user;
      return participant;
    }

    const { theme } = this.social;
    if (!theme) {
      this.error('Theme is undefined, cannot construct player');
      return;
    }
    if (!this.sceneService.sceneManager) {
      this.error('sceneManager is undefined, cannot construct player');
      return;
    }
    const player = new Player({
      store: this.store,
      renderer: this.sceneService.sceneManager.app!.renderer as unknown as Renderer,
      user,
      ringColor: user.ringColor,
      isPrimary: false,
      browserService: this.browserService,
      chatService: this.chatService,
      badgingService: this.badgingService,
      rtcStatsService: this.rtcStatsService,
      videoElement: undefined,
      x,
      y,
      bounds: this.sceneService.sceneManager.bounds,
      emoteService: this.emoteService,
      sprites: this.sceneService.sceneManager.sprites,
    });
    this.attachPlayerListeners(player);

    sceneManager.addParticipant(player);
    return player;
  }

  private attachPlayerListeners(player: Player): void {
    player
      .on(
        'pointerleave',
        () => {
          if (this.sceneService.sceneManager?.customCursorVisible) {
            return;
          }
          this.sceneService.sceneManager?.reRenderCustomCursor();
          this.sceneService.sceneManager?.showCustomCursor();
          player.handlePointerOut();
        },
        this,
      )
      .on('pointerenter', () => this.handlePointerOver(player), this)
      .on('pointermove', () => this.handlePointerOver(player), this);

    player.playerVisibility.pipe(takeUntil(this.destroyed$)).subscribe((visible) => {
      if (visible) {
        this.sceneService!.sceneManager!.playerInView({ player, visible: true }).catch((e) =>
          this.logger.error('Error in isPlayerWithinBounds > playerInView', e),
        );
      }
      if (this.sceneService.sceneManager) {
        this.sceneService.sceneManager
          .playerInView({ player, visible })
          .catch((e) => this.logger.error('Error in setPlayerVisibility > playerInView', e));
      }
    });
  }

  private handlePointerOver(player: Player): void {
    if (this.sceneService.sceneManager?.customCursorVisible) {
      this.sceneService.sceneManager.hideCustomCursor();
      player.handlePointerOver(this.sceneService.sceneManager.currentZoom);
    }
  }

  private addParticipantSoundElementToSocial(
    audioElement: HTMLAudioElement,
    subscriberSid: string,
    mainPlayer = false,
  ): void {
    const m = this.sceneService.sceneManager;
    if (audioElement && audioElement.srcObject && m) {
      const p = mainPlayer ? m.player : m.getParticipant(subscriberSid);

      if (!p) {
        return;
      }

      const audioContext = this.soundControl.audioContext;
      if (!audioContext) {
        return;
      }
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 32;

      const sound = {
        echoDataArray: new Uint8Array(analyser.frequencyBinCount),
        analyser: analyser,
        echoSize: PLAYER_RADIUS,
        echoAlpha: 0.4,
        lastEchoAmp: 128,
      };

      p.audioElement = audioElement;
      p.setSound(sound);
      this.multimediaRoomService.setAllVideoParticipantsSound();

      audioContext
        .resume()
        .then(() => {
          const { sound: newSound } = p;
          // Disconnect any existing audio source.
          if (newSound) {
            newSound?.audioSource?.disconnect(newSound.analyser);
            // Create a new audio source for the passed stream, and connect it to the analyser.
            const audioSource = audioContext.createMediaStreamSource(audioElement.srcObject as MediaStream);
            audioSource.connect(newSound.analyser);
            newSound.audioSource = audioSource;
          }
        })
        .catch((e: Error) => {
          this.error('Failed to resume audio context', e);
        });
    }
  }

  private removeParticipantFromWorld(userId: string) {
    const m = this.sceneService.sceneManager;
    if (!m) {
      return;
    }
    m.removeParticipant(userId);
  }

  private clearAdmissionInterval(): void {
    if (this.admissionIntervalTimeout) {
      clearInterval(this.admissionIntervalTimeout);
    }
    this.admissionIntervalTimeout = undefined;
  }

  public async captureDebugData(): Promise<void> {
    this.logUserAction('Capture Debug Data', 'clicked');
    await this.debuggerService.captureDebugData();
  }

  private async showTransitionOverlay(): Promise<void> {
    await delayFor(500);
    this.transitionOverlay = true;
  }

  private async hideTransitionOverlay(timeout = 3000): Promise<void> {
    await delayFor(timeout);
    this.transitionOverlay = false;
  }

  // Function to prevent invite dialog to appear before transition screen
  private async delayedShowInviteDialog(): Promise<void> {
    await delayFor(4000);
    this.showInviteDialog();
  }

  public get closedCaptionsEnabled(): boolean {
    return this.closedCaptionsService.closedCaptionsEnabled;
  }

  private requestPreviousChatMessages() {
    const todayInMs = new Date().getTime();
    const msg: ChatGetMessage = {
      t: OutgoingMessageType.CHATGET,
      beforeDate: todayInMs,
      maxCount: 20,
    };
    this.websocketService.sendMessage(msg);
  }

  public get themeBackgroundColor(): string {
    return this.social && this.social.theme?.colorBackground ? this.social.theme?.colorBackground : '';
  }

  public getShouldOverlay() {
    return (
      this.transitionService.showOverlay ||
      this.transitionPresentationService.showIconOverlay ||
      this.transitionPresentationService.showFullOverlay
    );
  }

  public getOverlayTransitionText() {
    if (this.transitionService.showOverlay) {
      return this.transitionService.overlayPresentationText;
    } else {
      return this.transitionPresentationService.overlayPresentationText;
    }
  }

  public getIsFullOverlay() {
    return this.transitionService.showOverlay || this.transitionPresentationService.showFullOverlay;
  }

  public getShouldBackgroundFade() {
    return this.transitionService.showFadeBackground || this.transitionPresentationService.showFadeBackground;
  }

  public getShouldBackgroundFadeIn() {
    return this.transitionService.showFadeInBackground || this.transitionPresentationService.showFadeInBackground;
  }

  public getBackgroundFadeInTimeoutTimeout() {
    if (this.transitionService.showFadeInBackground) {
      return 1500;
    } else {
      return this.transitionPresentationService.transitionTimers.backgroundFadeInTimeout;
    }
  }

  public getTransitionTimeout() {
    if (this.transitionService.showOverlay) {
      return DEFAULT_GROUP_SHARE_TIMEOUTS.presentationAudienceTransition - 500;
    } else {
      return this.transitionPresentationService.transitionTimers.presentationIconFadeIn - 1000;
    }
  }

  public closeSagModal() {
    if (this.isSAGModalOpen && !this.isSAGModalRequestAccess) {
      this.store.dispatch(actions.UPDATE_SAG_MODAL({ open: false, mode: SpecialAccessGroupModalMode.ALLOW_ACCESS }));
    }
  }

  // Function to trigger transition overlay on waiting for host state
  public triggerTransitionOverlay(shouldShow: boolean): void {
    if (shouldShow) {
      this.waitingForHostStep.set(undefined);
      void this.showTransitionOverlay();
    }
  }
}
